import styled from '@emotion/styled';
import { format } from 'date-fns';
import { TableProps, TableWrapper } from '@/components/common/elements/Table';
import NoticeTypeChip from '@/pages/Notice/components/NoticeTypeChip';
import { designSystem } from '@/styles/theme';
import { UI_KIND, uiTargetAttrs } from '@/common/uiEventNames';

interface NoticeTableRow {
  id: number;
  noticeType: string;
  title: string;
  registerDate: string;
  /** 서버가 아직 주지 않는 옛 응답이면 없음 — 그때는 조회 칸을 생략한다 */
  viewCount?: number;
}

/*
 * 공지 목록 행 — 속닥속닥 목록 행(PostTable)과 같은 틀: 제목 한 줄 + 메타 줄, 아래 구분선, 표 머리줄 없음.
 * 제목 #1f2937·메타 #6b7280·구분선 #e5e7eb 는 속닥속닥 행과 같은 값을 그대로 쓴다(두 목록의 행이 같아 보이도록).
 */
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }

  &:focus-visible {
    outline: 2px solid ${designSystem.colors.primary};
    outline-offset: -2px;
  }

  @media (max-width: 768px) {
    padding: 12px 0;
    gap: 9px;
  }
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;

  @media (max-width: 768px) {
    gap: 7px;
    margin-top: 4px;
    font-size: 12px;
  }
`;

const formatDay = (value: string): string => {
  const date = new Date(value);
  return value && !isNaN(date.getTime()) ? format(date, 'yyyy.MM.dd') : '-';
};

const NoticeTable = ({ data, onClick }: TableProps<NoticeTableRow>) => {
  return (
    <TableWrapper>
      {data?.map((row) => (
        <Row
          key={row.id}
          {...uiTargetAttrs(UI_KIND.NOTICE, row.id)}
          role="link"
          tabIndex={0}
          onClick={() => onClick(row.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick(row.id);
            }
          }}
        >
          <Main>
            <Title>{row.title}</Title>
            <Meta>
              <NoticeTypeChip noticeType={row.noticeType} />
              <span>{formatDay(row.registerDate)}</span>
              {typeof row.viewCount === 'number' && <span>조회 {row.viewCount.toLocaleString()}</span>}
            </Meta>
          </Main>
        </Row>
      ))}
    </TableWrapper>
  );
};

export default NoticeTable;
